import { HttpClient } from '@angular/common/http';
import { Component, effect, inject, signal } from '@angular/core';
import { GrunddatenService } from '../../../grundlagen/grunddaten.service';
import { environment } from '../../../../environment';
import { GridModule } from '@progress/kendo-angular-grid';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TreeData } from '../../../grundlagen/TreeData';
import { KENDO_TREEVIEW, NodeClickEvent } from '@progress/kendo-angular-treeview';
import { Observable, of } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-videos',
  standalone: true,
  imports: [GridModule, KENDO_TREEVIEW, CommonModule],
  templateUrl: './videos.component.html',
  styles: [
    `
      .leaf-node {
        color: blue;
      }
    `,
  ],
})
export class VideosComponent {
  // Signals für State-Management
  public erg = signal<TreeData[]>([]);
  public videoUrl = signal<string>('');
  public safeVideoUrl = signal<SafeResourceUrl | null>(null);
  public expandedKeys = signal<string[]>([]);

  private httpClient = inject(HttpClient);
  private grunddaten = inject(GrunddatenService);
  public sanitizer = inject(DomSanitizer);

  constructor() {
    // Daten beim Initialisieren laden
    this.loadVideos();

    // Effect für automatisches Sanitizen der Video-URL
    effect(() => {
      const url = this.videoUrl();
      if (url) {
        this.safeVideoUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
      } else {
        this.safeVideoUrl.set(null);
      }
    });
  }

  private loadVideos(): void {
    this.httpClient.get<{ weitere: TreeData[] }>(environment.baseUrl() + 'Video/liste').subscribe({
      next: (response) => {
        // console.log('Fetched data:', response);
        this.erg.set(response.weitere);
      },
      error: (err) => {
        //  console.error('Error loading videos:', err.message);
      },
    });
  }

  // Observable-basierte Funktionen für Kendo TreeView
  public fetchChildren: (item: any) => Observable<any[]> = (node) => {
    return of(node?.weitere ?? []);
  };

  public hasChildren: (item: any) => boolean = (node) => {
    return !!node?.weitere && node.weitere.length > 0;
  };

  public onNodeClick(event: NodeClickEvent): void {
    const item = event.item?.dataItem as TreeData | undefined;
    if (!item?.id) return;

    const key = item.id;

    // Video-Datei angeklickt
    if (key.endsWith('.mp4')) {
      const newUrl = environment.baseUrl() + 'video/anzeige?video=' + encodeURIComponent(key);

      // Nur aktualisieren wenn sich URL geändert hat
      if (this.videoUrl() !== newUrl) {
        this.videoUrl.set(newUrl);
      }
    } else {
      // Ordner angeklickt - expandieren/kollabieren
      this.toggleExpanded(key);
    }
  }

  private toggleExpanded(key: string): void {
    this.expandedKeys.update((keys) => {
      const index = keys.indexOf(key);

      if (index === -1) {
        // Key hinzufügen
        return [...keys, key];
      } else {
        // Key entfernen
        return keys.filter((k) => k !== key);
      }
    });

    // console.log('Expanded keys:', this.expandedKeys());
  }

  // Hilfsfunktion um zu prüfen ob ein Node ein Video ist
  public isVideoNode = (node: TreeData): boolean => {
    return node.id?.endsWith('.mp4') ?? false;
  };

  // Hilfsfunktion um CSS-Klasse für Nodes zu bestimmen
  public getNodeClass = (node: TreeData): string => {
    return this.isVideoNode(node) ? 'leaf-node' : '';
  };
}
